import { useLayoutEffect, useRef, useState } from "react";
import type { Effects, Element, TableElement, Transform } from "@deckloom/core/schema";
import { fillPaint, strokeAttrs, type MediaSize } from "./fills";
import { useFontsTick } from "./fonts";
import { geometryPath, STROKE_ONLY_PRESETS } from "./geometry";
import { TextBodyView } from "./TextBody";
import { usePlayer } from "./context";

export function transformAttr(t: Transform): string {
  const cx = t.x + t.w / 2, cy = t.y + t.h / 2;
  const parts = [`translate(${cx} ${cy})`];
  if (t.rot) parts.push(`rotate(${t.rot})`);
  if (t.flipH || t.flipV) parts.push(`scale(${t.flipH ? -1 : 1} ${t.flipV ? -1 : 1})`);
  parts.push(`translate(${-t.w / 2} ${-t.h / 2})`);
  return parts.join(" ");
}

/**
 * One element. The outer <g class="anim" data-el> is the only node the animation engine touches;
 * the inner <g> carries the static layout transform from the source.
 */
export function ElementView({ el, animated = true }: { el: Element; animated?: boolean }) {
  const { mediaUrl, deck } = usePlayer();
  if (el.hidden) return null;
  const t = el.transform;
  const mediaSize = (id: string) => deck.media[id];
  const fx = "effects" in el ? effectFilter(el.effects, el.id) : null;
  // text with a box of its own (SmartArt) is drawn beside the shape, in that box, with its own rotation
  const tt = (el.kind === "shape" || el.kind === "textbox") && el.text && el.textTransform ? el.textTransform : null;
  const inner = (
    <>
      <g transform={transformAttr(t)} filter={fx ? `url(#${fx.id})` : undefined}>
        {fx?.defs}
        {renderBody(el, mediaUrl, mediaSize, !tt)}
      </g>
      {tt && (el.kind === "shape" || el.kind === "textbox") && el.text && (
        <g transform={transformAttr(tt)}>
          <foreignObject x={0} y={0} width={tt.w} height={tt.h} style={{ overflow: "visible" }}>
            <TextBodyView body={el.text} elementId={el.id} mediaUrl={mediaUrl} />
          </foreignObject>
        </g>
      )}
    </>
  );
  if (!animated) return inner;
  return (
    <g className="anim" data-el={el.id} style={{ transformBox: "fill-box", transformOrigin: "center" }}>
      {inner}
    </g>
  );
}

function renderBody(el: Element, mediaUrl: (id: string) => string, mediaSize: (id: string) => MediaSize | undefined, withText = true) {
  const { w, h } = el.transform;
  switch (el.kind) {
    case "shape":
    case "textbox": {
      const fp = fillPaint(el.fill, el.id, mediaUrl, mediaSize);
      const st = strokeAttrs(el.stroke, el.id, mediaUrl);
      const d = geometryPath(el.geometry, w, h);
      const strokeOnly = el.geometry.kind === "preset" && STROKE_ONLY_PRESETS.has(el.geometry.preset);
      return (
        <>
          {(fp.defs || st.defs) && <defs>{fp.defs}{st.defs}</defs>}
          <path d={d} fill={strokeOnly ? "none" : fp.paint} fillOpacity={fp.opacity} {...st.attrs} vectorEffect="non-scaling-stroke" />
          {el.text && withText && (
            <foreignObject x={0} y={0} width={w} height={h} style={{ overflow: "visible" }}>
              <TextBodyView body={el.text} elementId={el.id} mediaUrl={mediaUrl} />
            </foreignObject>
          )}
        </>
      );
    }
    case "picture": {
      const st = strokeAttrs(el.stroke, el.id, mediaUrl);
      const clipId = el.geometry && !(el.geometry.kind === "preset" && el.geometry.preset === "rect") ? `clip-${el.id}` : null;
      const ref = el.crop;
      const image = ref ? (
        // Crop via a nested viewport: the image is drawn at an enlarged size and the viewport shows the kept region.
        <svg x={0} y={0} width={w} height={h} viewBox={`${ref.l * w} ${ref.t * h} ${(1 - ref.l - ref.r) * w} ${(1 - ref.t - ref.b) * h}`} preserveAspectRatio="none">
          <image href={mediaUrl(el.mediaId)} x={0} y={0} width={w} height={h} preserveAspectRatio="none" />
        </svg>
      ) : (
        <image href={mediaUrl(el.mediaId)} x={0} y={0} width={w} height={h} preserveAspectRatio="none" />
      );
      return (
        <>
          {clipId && (
            <defs>
              <clipPath id={clipId}><path d={geometryPath(el.geometry, w, h)} /></clipPath>
            </defs>
          )}
          <g clipPath={clipId ? `url(#${clipId})` : undefined}>{image}</g>
          {el.stroke && <path d={geometryPath(el.geometry, w, h)} fill="none" {...st.attrs} />}
        </>
      );
    }
    case "media": {
      // The SVG only shows the poster; playback lives in the HTML media layer above the slide.
      return (
        <>
          {el.poster ? (
            <image href={mediaUrl(el.poster)} x={0} y={0} width={w} height={h} preserveAspectRatio="none" />
          ) : (
            <rect width={w} height={h} fill="#111" />
          )}
          {el.mediaType === "video" && (
            <g transform={`translate(${w / 2 - 20} ${h / 2 - 20})`} opacity={0.85}>
              <circle cx={20} cy={20} r={20} fill="rgba(0,0,0,0.55)" />
              <path d="M15,11 L29,20 L15,29 Z" fill="#fff" />
            </g>
          )}
        </>
      );
    }
    case "group":
      return (
        <g transform={groupTransform(el)}>
          {el.children.map((c) => <ElementView key={c.id} el={c} />)}
        </g>
      );
    case "line": {
      const st = strokeAttrs(el.stroke, el.id, mediaUrl);
      return (
        <>
          {st.defs && <defs>{st.defs}</defs>}
          <path d={geometryPath(el.geometry, w, h)} fill="none" {...st.attrs} markerEnd={el.stroke.tail && el.stroke.tail.type !== "none" ? `url(#arrow-t-${el.id})` : undefined} markerStart={el.stroke.head && el.stroke.head.type !== "none" ? `url(#arrow-h-${el.id})` : undefined} />
          {(el.stroke.head?.type ?? "none") !== "none" && lineEndMarker(`arrow-h-${el.id}`, el.stroke.head!, el.stroke.width, st.attrs.stroke)}
          {(el.stroke.tail?.type ?? "none") !== "none" && lineEndMarker(`arrow-t-${el.id}`, el.stroke.tail!, el.stroke.width, st.attrs.stroke)}
        </>
      );
    }
    case "table":
      return <TableView el={el} mediaUrl={mediaUrl} />;
    case "unsupported":
      // Pictures in formats browsers cannot show (EMF/WMF) leave an empty spot like a missing image would, not a label.
      if (el.what === "other" && !el.fallbackMediaId) return null;
      return (
        <>
          {el.fallbackMediaId ? (
            <image href={mediaUrl(el.fallbackMediaId)} width={w} height={h} preserveAspectRatio="none" />
          ) : (
            <>
              <rect width={w} height={h} fill="#f3f4f6" stroke="#d1d5db" strokeDasharray="4 4" />
              <foreignObject width={w} height={h}>
                <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", color: "#6b7280", fontSize: 12, fontFamily: "sans-serif", textAlign: "center", padding: 8, boxSizing: "border-box" }}>
                  {el.label}
                </div>
              </foreignObject>
            </>
          )}
        </>
      );
  }
}

/** Rows grow to fit their text like in PowerPoint: cell contents are measured after layout (and again once fonts load). */
function TableView({ el, mediaUrl }: { el: TableElement; mediaUrl: (id: string) => string }) {
  const ref = useRef<SVGGElement>(null);
  const [rows, setRows] = useState<number[]>(el.rowHeights);
  const fontsTick = useFontsTick();
  useLayoutEffect(() => { setRows(el.rowHeights); }, [el]);
  useLayoutEffect(() => {
    const g = ref.current;
    if (!g) return;
    const next = el.rowHeights.slice();
    g.querySelectorAll<SVGForeignObjectElement>("foreignObject[data-row]").forEach((fo) => {
      const ri = Number(fo.dataset.row), span = Number(fo.dataset.span ?? 1);
      const inner = fo.querySelector<HTMLElement>(".tb-inner");
      if (!inner || span !== 1) return;
      const need = inner.offsetHeight + Number(fo.dataset.padY ?? 0);
      if (need > (next[ri] ?? 0)) next[ri] = need;
    });
    if (next.some((h, i) => Math.abs(h - (rows[i] ?? 0)) > 0.5)) setRows(next);
  }, [el, rows, fontsTick]);

  let y = 0;
  const out = el.cells.map((row, ri) => {
    const rh = rows[ri] ?? 0;
    let x = 0;
    const cells = row.map((cell, ci) => {
      const cw = el.colWidths[ci] ?? 0;
      const cx = x;
      x += cw;
      if (cell.covered) return null;
      const spanW = el.colWidths.slice(ci, ci + (cell.colSpan ?? 1)).reduce((a, b) => a + b, 0);
      const spanH = rows.slice(ri, ri + (cell.rowSpan ?? 1)).reduce((a, b) => a + b, 0);
      const fp = fillPaint(cell.fill, `${el.id}-${ri}-${ci}`, mediaUrl);
      const border = (st: typeof cell.borders.l, d: string) =>
        st ? <path d={d} {...strokeAttrs(st, `${el.id}-${ri}-${ci}-b`, mediaUrl).attrs} /> : null;
      return (
        <g key={ci} transform={`translate(${cx} ${y})`}>
          {fp.defs && <defs>{fp.defs}</defs>}
          <rect width={spanW} height={spanH} fill={fp.paint} fillOpacity={fp.opacity} />
          <foreignObject width={spanW} height={spanH} style={{ overflow: "visible" }} data-row={ri} data-span={cell.rowSpan ?? 1} data-pad-y={cell.insets.t + cell.insets.b}>
            <TextBodyView body={{ ...cell.text, insets: cell.insets }} elementId={`${el.id}-${ri}-${ci}`} mediaUrl={mediaUrl} />
          </foreignObject>
          {border(cell.borders.t, `M0,0 L${spanW},0`)}
          {border(cell.borders.b, `M0,${spanH} L${spanW},${spanH}`)}
          {border(cell.borders.l, `M0,0 L0,${spanH}`)}
          {border(cell.borders.r, `M${spanW},0 L${spanW},${spanH}`)}
        </g>
      );
    });
    y += rh;
    return <g key={ri}>{cells}</g>;
  });
  return <g ref={ref}>{out}</g>;
}

/** Outer shadow, glow and soft edge as an SVG filter (inner shadows and reflections are not shown). */
function effectFilter(effects: Effects | null | undefined, id: string): { id: string; defs: JSX.Element } | null {
  if (!effects) return null;
  const { shadow, glow, softEdge } = effects;
  const outer = shadow && shadow.kind === "outer" ? shadow : undefined;
  if (!outer && !glow && !softEdge) return null;
  const fid = `fx-${id}`;
  const steps: JSX.Element[] = [];
  const merge: string[] = [];
  if (glow) {
    const c = glow.color;
    steps.push(
      <feMorphology key="g1" in="SourceAlpha" operator="dilate" radius={Math.max(0, glow.radius * 0.5)} result="glowSpread" />,
      <feGaussianBlur key="g2" in="glowSpread" stdDeviation={Math.max(0.5, glow.radius * 0.5)} result="glowBlur" />,
      <feFlood key="g3" floodColor={c.hex} floodOpacity={c.alpha} result="glowColor" />,
      <feComposite key="g4" in="glowColor" in2="glowBlur" operator="in" result="glow" />,
    );
    merge.push("glow");
  }
  if (outer) {
    const a = (outer.angle * Math.PI) / 180;
    const dx = Math.cos(a) * outer.dist, dy = Math.sin(a) * outer.dist;
    steps.push(
      <feGaussianBlur key="s1" in="SourceAlpha" stdDeviation={Math.max(0, outer.blur / 2)} result="shadowBlur" />,
      <feOffset key="s2" in="shadowBlur" dx={dx} dy={dy} result="shadowOffset" />,
      <feFlood key="s3" floodColor={outer.color.hex} floodOpacity={outer.color.alpha} result="shadowColor" />,
      <feComposite key="s4" in="shadowColor" in2="shadowOffset" operator="in" result="shadow" />,
    );
    merge.push("shadow");
  }
  let source = "SourceGraphic";
  if (softEdge && softEdge.radius > 0) {
    steps.push(
      <feGaussianBlur key="e1" in="SourceAlpha" stdDeviation={softEdge.radius / 2} result="softAlpha" />,
      <feComponentTransfer key="e2" in="softAlpha" result="softMask"><feFuncA type="linear" slope={2} intercept={-0.5} /></feComponentTransfer>,
      <feComposite key="e3" in="SourceGraphic" in2="softMask" operator="in" result="soft" />,
    );
    source = "soft";
  }
  merge.push(source);
  const pad = Math.max(10, (outer ? outer.blur + outer.dist : 0) + (glow ? glow.radius : 0) + (softEdge?.radius ?? 0)) * 2;
  const defs = (
    <defs>
      <filter id={fid} x={-pad} y={-pad} width="100%" height="100%" filterUnits="userSpaceOnUse" style={{ width: `calc(100% + ${pad * 2}px)`, height: `calc(100% + ${pad * 2}px)` }} colorInterpolationFilters="sRGB">
        {steps}
        <feMerge>{merge.map((m) => <feMergeNode key={m} in={m} />)}</feMerge>
      </filter>
    </defs>
  );
  return { id: fid, defs };
}

/** Line-end sizes in stroke widths (ECMA-376: sm 2x, med 3x, lg 5x). */
function markerSize(v: "sm" | "med" | "lg" | undefined): number {
  return v === "sm" ? 2 : v === "lg" ? 5 : 3;
}

/** Arrowhead marker. Sizes follow the line width, but hairlines still get a visible head like in PowerPoint. */
function lineEndMarker(id: string, end: { type: string; w: "sm" | "med" | "lg"; len: "sm" | "med" | "lg" }, width: number, color: string | undefined) {
  const base = Math.max(width, 2);
  const mw = markerSize(end.len) * base, mh = markerSize(end.w) * base;
  const open = end.type === "arrow";
  return (
    <defs>
      <marker id={id} viewBox="0 0 10 10" refX="9" refY="5" markerWidth={mw} markerHeight={mh} markerUnits="userSpaceOnUse" orient="auto-start-reverse">
        <path d={arrowPath(end.type)} fill={open ? "none" : color} stroke={color} strokeWidth={open ? 10 / Math.max(mw, 1) * Math.max(width, 0.75) : 0} strokeLinejoin="miter" />
      </marker>
    </defs>
  );
}
function arrowPath(type: string | undefined): string {
  switch (type) {
    case "stealth": return "M0,0 L10,5 L0,10 L3,5 Z";
    case "diamond": return "M5,0 L10,5 L5,10 L0,5 Z";
    case "oval": return "M5,0 A5,5 0 1 1 5,10 A5,5 0 1 1 5,0 Z";
    case "arrow": return "M1,1 L9,5 L1,9";
    default: return "M0,0 L10,5 L0,10 Z";
  }
}

function groupTransform(el: Extract<Element, { kind: "group" }>): string {
  const { childSpace, transform } = el;
  const sx = childSpace.w ? transform.w / childSpace.w : 1;
  const sy = childSpace.h ? transform.h / childSpace.h : 1;
  return `scale(${sx} ${sy}) translate(${-childSpace.x} ${-childSpace.y})`;
}
